import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Dropdown, Modal } from "react-bootstrap";
import { saveAs } from "file-saver";
import { GET, POST, PUT, loading, alert } from "../CustomComponent";
import { loadEditorFonts } from "../pdf-editor/fonts";
import { loadImage, prepareImage, readAsDataUrl } from "../pdf-editor/imageUtils";
import { formatToday, waitForSwalClose } from "../pdf-editor/textUtils";
import PageView, { DEFAULT_TEXT } from "../pdf-editor/PageView";
import AssetLibraryModal from "../pdf-editor/AssetLibraryModal";
import { copyObjects, hasClipboard, pasteObjects } from "../pdf-editor/objectClipboard";
import CropOverlay, { fitRatioRect } from "./CropOverlay";
import {
  DEFAULT_ADJUST, DEFAULT_WATERMARK, FILTERS, applyAdjust, canvasToBlob, canvasToBmpBlob, cloneCanvas, cropCanvas, ensureImages,
  exceedsCanvasLimit, flattenOnWhite, flipCanvas, makeCanvas, makePreviewBase, renderPipeline, resizeCanvas, rotateCanvas,
  transformObjects, unitOf,
} from "./imagePipeline";

const PRIMARY = "#0c76e5";
const INK = "#22384a";
const MAX_HISTORY = 25;
const MIN_ZOOM = 0.05;
const MAX_ZOOM = 8;
const BIG_PIXELS = 40 * 1000 * 1000;

const TOOL_DEFAULTS = {
  pen: { color: "#dc2626", width: 3 },
  highlight: { color: "#ffe600", width: 20 },
  rect: { color: "#dc2626", width: 3 },
  text: { color: "#111827" },
};

const ASPECTS = [
  { key: "free", label: "อิสระ", ratio: null },
  { key: "1:1", label: "1:1", ratio: 1 },
  { key: "4:3", label: "4:3", ratio: 4 / 3 },
  { key: "16:9", label: "16:9", ratio: 16 / 9 },
  { key: "3:2", label: "3:2", ratio: 3 / 2 },
];

const FORMATS = [
  { mime: "image/jpeg", label: "JPG", ext: "jpg", lossy: true },
  { mime: "image/png", label: "PNG", ext: "png", lossy: false },
  { mime: "image/webp", label: "WEBP", ext: "webp", lossy: true },
];

let idSeed = 0;
const nextId = () => {
  idSeed += 1;
  return `i${Date.now().toString(36)}${idSeed}`;
};

const normalizeMime = (mime) => {
  const m = String(mime || "").toLowerCase();
  return m === "image/jpg" || m === "image/pjpeg" ? "image/jpeg" : m;
};

function ToolButton({ icon, label, active, onClick, disabled }) {
  return (
    <button
      type="button"
      className="btn btn-sm d-flex flex-column align-items-center justify-content-center px-1 py-1 border-0 w-100"
      style={{ minHeight: 50, borderRadius: 10, background: active ? "rgba(12,118,229,0.14)" : "transparent", color: active ? PRIMARY : INK, fontWeight: active ? 600 : 400 }}
      onClick={onClick}
      disabled={disabled}
      title={label}
    >
      <i className={`fa-solid ${icon}`} style={{ fontSize: 16 }} />
      <span style={{ fontSize: 11, marginTop: 2, whiteSpace: "nowrap" }}>{label}</span>
    </button>
  );
}

function Slider({ label, value, min, max, step = 1, onChange, suffix = "" }) {
  return (
    <label className="d-block small mb-3">
      <div className="d-flex justify-content-between"><span>{label}</span><span className="text-secondary">{Math.round(value * 100) / 100}{suffix}</span></div>
      <input type="range" className="form-range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </label>
  );
}

const Section = ({ title, children }) => (
  <div className="mb-3">
    {title ? <div className="fw-semibold small mb-2">{title}</div> : null}
    {children}
  </div>
);

// file: { uid, name, folder_uid, mime_type } จาก CompanyDrive
export default function ImageEditor({ file, onClose, onSaved }) {
  const [doc, setDoc] = useState(null); // { base: canvas, objects, adj, wm }
  const [, setTick] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [tool, setTool] = useState("select");
  const [style, setStyle] = useState({ color: "#dc2626", width: 3, textSize: 40 });
  const [selectedIds, setSelectedIds] = useState([]);
  const [, setClipTick] = useState(0);
  const editorId = useRef(nextId()).current;
  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null; // แก้คุณสมบัติได้เฉพาะตอนเลือกชิ้นเดียว
  const setSelectedId = useCallback((id) => setSelectedIds(id ? [id] : []), []);
  const [assetType, setAssetType] = useState(null);
  const [previewCanvas, setPreviewCanvas] = useState(null);
  const [origMime, setOrigMime] = useState("image/png");
  const [notes, setNotes] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveDialog, setSaveDialog] = useState(null); // "new" | "download"
  const [fmt, setFmt] = useState("image/png");
  const [quality, setQuality] = useState(90);
  const [cropRect, setCropRect] = useState(null);
  const [cropAspect, setCropAspect] = useState("free");
  const [resizeW, setResizeW] = useState("");
  const [resizeH, setResizeH] = useState("");
  const [resizeLock, setResizeLock] = useState(true);
  const [redactMode, setRedactMode] = useState("blur");
  const [redactStrength, setRedactStrength] = useState(12);

  const docRef = useRef(null);
  const savedRef = useRef(null);
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  const lastEditKey = useRef(null);
  const areaRef = useRef(null);
  const imageInputRef = useRef(null);
  const textAreaRef = useRef(null);
  const adjustCache = useRef({ canvas: null, adj: null, result: null });
  const closedRef = useRef(false);

  const dirty = doc !== savedRef.current;
  const base = doc?.base;
  const unit = base ? unitOf(base) : 1;

  /* ---------- state + undo/redo ---------- */

  const pushHistory = useCallback(() => {
    undoRef.current.push(docRef.current);
    if (undoRef.current.length > MAX_HISTORY) undoRef.current.shift();
    redoRef.current = [];
    lastEditKey.current = null;
    setTick((t) => t + 1);
  }, []);

  const commit = useCallback((next, record = true) => {
    if (record) pushHistory();
    docRef.current = next;
    setDoc(next);
  }, [pushHistory]);

  const undo = useCallback(() => {
    const prev = undoRef.current.pop();
    if (!prev) return;
    redoRef.current.push(docRef.current);
    lastEditKey.current = null;
    docRef.current = prev;
    setDoc(prev);
    setSelectedId(null);
    setTick((t) => t + 1);
  }, [setSelectedId]);

  const redo = useCallback(() => {
    const next = redoRef.current.pop();
    if (!next) return;
    undoRef.current.push(docRef.current);
    lastEditKey.current = null;
    docRef.current = next;
    setDoc(next);
    setSelectedId(null);
    setTick((t) => t + 1);
  }, [setSelectedId]);

  const mapObjects = (fn) => ({ ...docRef.current, objects: fn(docRef.current.objects) });

  const addObject = useCallback((pageId, obj) => {
    const id = nextId();
    commit(mapObjects((list) => [...list, { ...obj, id }]));
    setSelectedId(id);
    if (obj.type === "text" || obj.type === "image") setTool("select");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commit]);

  const updateMany = useCallback((pageId, patches) => {
    commit(mapObjects((list) => list.map((o) => (patches[o.id] ? { ...o, ...patches[o.id] } : o))), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commit]);

  const deleteSelected = useCallback(() => {
    if (!selectedIds.length) return;
    commit(mapObjects((list) => list.filter((o) => !selectedIds.includes(o.id))));
    setSelectedIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commit, selectedIds]);

  const pageInfo = () => ({ W: docRef.current.base.width, H: docRef.current.base.height, R: 0 });

  const copySelected = useCallback(() => {
    if (!selectedIds.length || !docRef.current) return;
    const objs = docRef.current.objects.filter((o) => selectedIds.includes(o.id));
    if (copyObjects(objs, pageInfo(), `${editorId}:img`)) setClipTick((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, editorId]);

  const pasteClip = useCallback(() => {
    if (!docRef.current) return;
    const objs = pasteObjects(pageInfo(), `${editorId}:img`, { newId: () => nextId() });
    if (!objs.length) return;
    commit(mapObjects((list) => [...list, ...objs]));
    setSelectedIds(objs.map((o) => o.id));
    setTool("select");
    setClipTick((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commit, editorId]);

  const selectAll = useCallback(() => {
    if (docRef.current?.objects.length) setSelectedIds(docRef.current.objects.map((o) => o.id));
  }, []);

  const selectedObj = useMemo(() => doc?.objects.find((o) => o.id === selectedId) || null, [doc, selectedId]);

  // แก้ค่าต่อเนื่อง (slider/สี) รวมเป็น 1 ขั้น undo
  const coalesced = (key, producer) => {
    if (lastEditKey.current !== key) {
      pushHistory();
      lastEditKey.current = key;
    }
    commit(producer(docRef.current), false);
  };

  const editSelected = (key, patch) => {
    if (!selectedObj) return;
    coalesced(`${selectedObj.id}:${key}`, (d) => ({ ...d, objects: d.objects.map((o) => (o.id === selectedObj.id ? { ...o, ...patch } : o)) }));
  };

  /* ---------- โหลดภาพ ---------- */

  useEffect(() => {
    let cancelled = false;
    (async () => {
      loading(true);
      let problem = "";
      try {
        const [result] = await Promise.all([GET(`v1/admin/drive/files/${file.uid}/content`, undefined, undefined, { silent: true }), loadEditorFonts().catch(() => null)]);
        if (cancelled) return;
        if (!result?.status || !result.data?.file_base64) {
          problem = result?.message || "เปิดไฟล์ไม่สำเร็จ";
        } else {
          const dataUrl = result.data.file_base64;
          const mime = normalizeMime(result.data.mime_type || file.mime_type || /^data:([^;,]+)/.exec(dataUrl)?.[1]);
          const img = await loadImage(dataUrl);
          if (cancelled) return;
          const w = img.naturalWidth;
          const h = img.naturalHeight;
          if (exceedsCanvasLimit(w, h)) {
            problem = `ภาพขนาด ${w}×${h} พิกเซลใหญ่เกินกว่าที่เบราว์เซอร์จะแก้ไขได้`;
          } else {
            const canvas = makeCanvas(w, h);
            canvas.getContext("2d").drawImage(img, 0, 0);
            const first = { base: canvas, objects: [], adj: DEFAULT_ADJUST, wm: DEFAULT_WATERMARK };
            const info = [];
            if (mime === "image/gif") info.push("GIF: แก้ไขได้เฉพาะเฟรมแรก บันทึกแล้วจะเป็นภาพนิ่ง");
            if (w * h > BIG_PIXELS) info.push(`ภาพใหญ่ ${(w * h / 1e6).toFixed(0)} ล้านพิกเซล: พรีวิวจะย่อ แต่บันทึกด้วยความละเอียดเต็ม`);
            setNotes(info);
            setOrigMime(mime);
            setFmt(["image/jpeg", "image/png", "image/webp"].includes(mime) ? mime : "image/png");
            setStyle((s) => ({ ...s, textSize: Math.round(Math.max(w, h) / 30) }));
            savedRef.current = first;
            docRef.current = first;
            setDoc(first);
          }
        }
      } catch (error) {
        problem = error?.message || "เปิดภาพไม่สำเร็จ";
      }
      loading(false); // ปิด overlay ก่อน alert เสมอ ไม่งั้นบังปุ่ม SweetAlert
      if (!cancelled && problem) {
        await alert("", "warning", "แจ้งเตือน", problem);
        onClose();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.uid]);

  /* ---------- พรีวิว ---------- */

  const previewBase = useMemo(() => (base ? makePreviewBase(base) : null), [base]);
  const redactKey = useMemo(() => JSON.stringify((doc?.objects || []).filter((o) => o.type === "redact")), [doc?.objects]);

  useEffect(() => {
    if (!doc || !previewBase) return undefined;
    const timer = setTimeout(() => {
      const { canvas, scale } = previewBase;
      const fullUnit = unitOf(doc.base);
      const cache = adjustCache.current;
      let adjusted;
      if (cache.canvas === canvas && cache.adj === doc.adj) {
        adjusted = cache.result;
      } else {
        adjusted = cloneCanvas(canvas);
        applyAdjust(adjusted, doc.adj, scale, fullUnit);
        adjustCache.current = { canvas, adj: doc.adj, result: adjusted };
      }
      setPreviewCanvas(renderPipeline({
        source: canvas, adjusted, scale, objects: doc.objects.filter((o) => o.type === "redact"), adj: doc.adj, wm: doc.wm, fullUnit, withObjects: false,
      }));
    }, 25);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewBase, doc?.adj, doc?.wm, redactKey]);

  /* ---------- ซูม ---------- */

  const fit = useCallback(() => {
    const area = areaRef.current;
    const b = docRef.current?.base;
    if (!area || !b) return;
    const z = Math.min((area.clientWidth - 48) / b.width, (area.clientHeight - 48) / b.height);
    setZoom(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z)));
  }, []);

  useEffect(() => {
    if (base) requestAnimationFrame(fit);
  }, [base, fit]);

  const stepZoom = (factor) => setZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z * factor)));

  /* ---------- ปิด / คีย์ลัด ---------- */

  const requestClose = useCallback(async () => {
    if (isSaving || closedRef.current) return;
    if (docRef.current !== savedRef.current) {
      const ok = await alert("", "warning", "ยังไม่ได้บันทึก", "มีการแก้ไขที่ยังไม่ได้บันทึก ต้องการปิดโดยไม่บันทึกหรือไม่?", "ปิดโดยไม่บันทึก", "กลับไปแก้ไข");
      if (!ok) return;
    }
    closedRef.current = true;
    onClose();
  }, [isSaving, onClose]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const tag = event.target?.tagName;
      const typing = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || event.target?.isContentEditable;
      const mod = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();
      if (typing || document.querySelector(".swal2-container")) return;
      if (mod && key === "z") {
        event.preventDefault();
        if (event.shiftKey) redo(); else undo();
      } else if (mod && key === "y") {
        event.preventDefault();
        redo();
      } else if (mod && key === "c" && selectedIds.length) {
        event.preventDefault();
        copySelected();
      } else if (mod && key === "v" && hasClipboard()) {
        event.preventDefault();
        pasteClip();
      } else if (mod && key === "a") {
        event.preventDefault();
        selectAll();
      } else if ((event.key === "Delete" || event.key === "Backspace") && selectedIds.length) {
        event.preventDefault();
        deleteSelected();
      } else if (event.key === "Escape") {
        setSelectedIds([]);
        setTool("select");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [undo, redo, deleteSelected, selectedIds, copySelected, pasteClip, selectAll]);

  useEffect(() => {
    if (selectedObj?.type === "text" && selectedObj.text === DEFAULT_TEXT && textAreaRef.current) {
      textAreaRef.current.focus();
      textAreaRef.current.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  /* ---------- เครื่องมือ ---------- */

  const chooseTool = (next) => {
    setTool(next);
    setSelectedId(null);
    if (TOOL_DEFAULTS[next]) setStyle((s) => ({ ...s, ...TOOL_DEFAULTS[next] }));
    if (next === "crop" && docRef.current) {
      setCropAspect("free");
      setCropRect({ x: 0, y: 0, w: docRef.current.base.width, h: docRef.current.base.height });
    }
  };

  const changeColor = (color) => {
    setStyle((s) => ({ ...s, color }));
    if (selectedObj && selectedObj.color !== undefined) editSelected("color", { color });
  };
  const changeWidth = (value) => {
    setStyle((s) => ({ ...s, width: value }));
    if (selectedObj && ["stroke", "highlight", "rect"].includes(selectedObj.type)) editSelected("width", { width: value * unit });
  };
  const changeTextSize = (raw) => {
    const value = Math.min(4000, Math.max(4, Number(raw) || 4));
    setStyle((s) => ({ ...s, textSize: value }));
    if (selectedObj?.type === "text") editSelected("size", { size: value });
  };

  /* ---------- แทรกรูป / ลายเซ็น / ข้อความ ---------- */

  const addImageFromDataUrl = async (dataUrl, widthRatio) => {
    const img = await loadImage(dataUrl);
    const b = docRef.current.base;
    const w = Math.min(b.width * widthRatio, img.width * Math.max(1, b.width / 1000));
    const h = (w * img.height) / img.width;
    addObject("img", { type: "image", x: (b.width - w) / 2, y: (b.height - h) / 2, rot: 0, w, h, src: dataUrl });
  };

  const handlePickAsset = async (dataUrl) => {
    const type = assetType;
    setAssetType(null);
    try {
      await addImageFromDataUrl(dataUrl, type === "stamp" ? 0.2 : 0.28);
    } catch (error) {
      await alert("", "warning", "แจ้งเตือน", error.message || "แทรกรูปไม่สำเร็จ");
    }
  };

  const handleImageFile = async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = "";
    if (!picked) return;
    if (!picked.type.startsWith("image/")) {
      await alert("", "warning", "แจ้งเตือน", "กรุณาเลือกไฟล์รูปภาพ");
      return;
    }
    try {
      const prepared = await prepareImage(picked);
      await addImageFromDataUrl(prepared.dataUrl, 0.4);
    } catch (error) {
      await alert("", "warning", "แจ้งเตือน", error.message || "แทรกรูปไม่สำเร็จ");
    }
  };

  const addDate = (era) => {
    const b = docRef.current.base;
    addObject("img", { type: "text", x: b.width * 0.35, y: b.height * 0.45, rot: 0, text: formatToday(era), size: style.textSize, color: style.color, bold: false });
  };

  /* ---------- เรขาคณิต ---------- */

  const applyGeometry = (newBase, op, dims) => {
    const d = docRef.current;
    commit({ ...d, base: newBase, objects: transformObjects(d.objects, op, dims) });
    setSelectedId(null);
  };

  const dimsOf = () => ({ w: docRef.current.base.width, h: docRef.current.base.height });

  const rotate = (dir) => applyGeometry(rotateCanvas(docRef.current.base, dir), { type: "rotate", dir }, dimsOf());
  const flip = (axis) => applyGeometry(flipCanvas(docRef.current.base, axis), { type: "flip", axis }, dimsOf());

  const applyCrop = () => {
    if (!cropRect) return;
    const r = {
      x: Math.round(cropRect.x), y: Math.round(cropRect.y),
      w: Math.max(1, Math.round(cropRect.w)), h: Math.max(1, Math.round(cropRect.h)),
    };
    r.w = Math.min(r.w, docRef.current.base.width - r.x);
    r.h = Math.min(r.h, docRef.current.base.height - r.y);
    applyGeometry(cropCanvas(docRef.current.base, r), { type: "crop", x: r.x, y: r.y }, dimsOf());
    setTool("select");
  };

  const chooseAspect = (item) => {
    setCropAspect(item.key);
    if (item.ratio && base) setCropRect(fitRatioRect(base.width, base.height, item.ratio));
  };

  useEffect(() => {
    if (base) {
      setResizeW(String(base.width));
      setResizeH(String(base.height));
    }
  }, [base]);

  const changeResize = (which, value) => {
    const n = value.replace(/\D/g, "");
    if (which === "w") {
      setResizeW(n);
      if (resizeLock && n && base) setResizeH(String(Math.max(1, Math.round((Number(n) * base.height) / base.width))));
    } else {
      setResizeH(n);
      if (resizeLock && n && base) setResizeW(String(Math.max(1, Math.round((Number(n) * base.width) / base.height))));
    }
  };

  const applyResize = async () => {
    const w = Number(resizeW);
    const h = Number(resizeH);
    if (!w || !h) return;
    if (exceedsCanvasLimit(w, h)) {
      await alert("", "warning", "แจ้งเตือน", "ขนาดใหญ่เกินที่เบราว์เซอร์รองรับ (ด้านละไม่เกิน 16,384 พิกเซล)");
      return;
    }
    const b = docRef.current.base;
    if (w === b.width && h === b.height) return;
    applyGeometry(resizeCanvas(b, w, h), { type: "resize", sx: w / b.width, sy: h / b.height }, dimsOf());
  };

  /* ---------- ปรับสี / ลายน้ำ ---------- */

  const setAdjust = (key, value) => coalesced(`adj:${key}`, (d) => ({ ...d, adj: { ...d.adj, [key]: value } }));
  const resetAdjust = () => { if (doc.adj !== DEFAULT_ADJUST) commit({ ...docRef.current, adj: DEFAULT_ADJUST }); };
  const setWm = (key, value) => coalesced(`wm:${key}`, (d) => ({ ...d, wm: { ...d.wm, [key]: value } }));

  /* ---------- บันทึก ---------- */

  const nameFor = (mime) => {
    const baseName = String(file.name || "image").replace(/\.(jpe?g|png|webp|bmp|gif)$/i, "");
    const keepJpeg = mime === "image/jpeg" && /\.jpeg$/i.test(file.name || "");
    const ext = keepJpeg ? "jpeg" : FORMATS.find((f) => f.mime === mime)?.ext || "png";
    return `${baseName}_edited.${ext}`;
  };

  const buildBlob = async (mime, q) => {
    const d = docRef.current;
    await ensureImages(d.objects);
    let canvas = renderPipeline({ source: d.base, scale: 1, objects: d.objects, adj: d.adj, wm: d.wm, fullUnit: unitOf(d.base), withObjects: true });
    if (mime === "image/bmp") return canvasToBmpBlob(canvas);
    if (mime === "image/jpeg") canvas = flattenOnWhite(canvas); // JPG ไม่มีพื้นโปร่ง
    const blob = await canvasToBlob(canvas, mime, q / 100);
    if (blob.type !== mime) throw new Error(`เบราว์เซอร์นี้บันทึกเป็น ${mime.replace("image/", "").toUpperCase()} ไม่ได้ กรุณาเลือกชนิดอื่น`);
    return blob;
  };

  const markSaved = async (message) => {
    savedRef.current = docRef.current;
    setTick((t) => t + 1);
    onSaved?.();
    await waitForSwalClose();
    await alert("", "success", "สำเร็จ", message);
  };

  const handleSave = async (mode, mime, q) => {
    if (isSaving) return;
    if (mode === "overwrite") {
      const ok = await alert("", "warning", "บันทึกทับไฟล์เดิม", `ไฟล์ "${file.name}" จะถูกแทนที่ด้วยเวอร์ชันที่แก้ไขนี้ และย้อนกลับไม่ได้ ต้องการดำเนินการต่อ?`, "บันทึกทับ", "ยกเลิก");
      if (!ok) return;
    }

    setIsSaving(true);
    loading(true);
    let problem = "";
    let done = "";
    try {
      const blob = await buildBlob(mime, q);
      const name = nameFor(mime);
      if (mode === "download") {
        saveAs(blob, name);
      } else {
        const dataUrl = await readAsDataUrl(blob);
        const result = mode === "overwrite"
          ? await PUT(`v1/admin/drive/files/${file.uid}/content`, { file_base64: dataUrl, mime_type: mime })
          : await POST("v1/admin/drive/files", {
              folder_uid: file.folder_uid || undefined,
              files: [{ file_name: name, mime_type: mime, file_base64: dataUrl }],
            });
        if (result?.status) done = mode === "overwrite" ? "บันทึกทับไฟล์เดิมแล้ว" : `บันทึกเป็นไฟล์ใหม่ "${name}" แล้ว`;
        else problem = result?.message || "บันทึกไม่สำเร็จ";
      }
    } catch (error) {
      problem = error?.message || "สร้างไฟล์ภาพไม่สำเร็จ";
    }
    loading(false); // ปิด overlay ก่อน alert เสมอ
    setIsSaving(false);
    if (problem) {
      await waitForSwalClose();
      await alert("", "warning", "แจ้งเตือน", problem);
    } else if (done) await markSaved(done);
  };

  const canOverwrite = origMime !== "image/gif";
  const overwriteMime = ["image/jpeg", "image/png", "image/webp", "image/bmp"].includes(origMime) ? origMime : "image/png";
  const fmtInfo = FORMATS.find((f) => f.mime === fmt) || FORMATS[1];

  /* ---------- ส่วนแสดงผล ---------- */

  const page = useMemo(() => (doc ? {
    id: "img", docId: "img", srcIndex: 0, baseRotation: 0, userRotation: 0, W: doc.base.width, H: doc.base.height, objects: doc.objects,
  } : null), [doc]);

  const isDrawTool = ["pen", "highlight", "rect", "text", "redact"].includes(tool);
  const showWidth = ["pen", "highlight", "rect"].includes(tool) || ["stroke", "highlight", "rect"].includes(selectedObj?.type);
  const showTextSize = tool === "text" || selectedObj?.type === "text";
  const showColor = ["pen", "highlight", "rect", "text"].includes(tool) || (selectedObj && selectedObj.color !== undefined);
  const widthValue = selectedObj && selectedObj.width !== undefined ? selectedObj.width / unit : style.width;

  const panel = (() => {
    if (!doc) return null;
    if (tool === "crop") {
      return (
        <>
          <Section title="อัตราส่วนครอป">
            <div className="d-flex flex-wrap gap-1">
              {ASPECTS.map((a) => (
                <button key={a.key} type="button" className={`btn btn-sm ${cropAspect === a.key ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => chooseAspect(a)}>{a.label}</button>
              ))}
            </div>
          </Section>
          {cropRect ? <div className="small text-secondary mb-3">ขนาดที่ครอป {Math.round(cropRect.w)} × {Math.round(cropRect.h)} พิกเซล</div> : null}
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-primary btn-sm" onClick={applyCrop}><i className="fa-solid fa-check me-1" />ใช้การครอป</button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => chooseTool("select")}>ยกเลิก</button>
          </div>
        </>
      );
    }
    if (tool === "transform") {
      return (
        <>
          <Section title="หมุน / พลิก">
            <div className="d-flex flex-wrap gap-1">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => rotate(-90)} title="หมุนซ้าย 90°"><i className="fa-solid fa-rotate-left me-1" />ซ้าย</button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => rotate(90)} title="หมุนขวา 90°"><i className="fa-solid fa-rotate-right me-1" />ขวา</button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => flip("h")} title="พลิกแนวนอน"><i className="fa-solid fa-left-right me-1" />แนวนอน</button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => flip("v")} title="พลิกแนวตั้ง"><i className="fa-solid fa-up-down me-1" />แนวตั้ง</button>
            </div>
          </Section>
          <Section title="ย่อ / ขยายขนาด (พิกเซล)">
            <div className="d-flex align-items-center gap-2 mb-2">
              <input type="text" inputMode="numeric" className="form-control form-control-sm" aria-label="กว้าง" value={resizeW} onChange={(e) => changeResize("w", e.target.value)} />
              <span>×</span>
              <input type="text" inputMode="numeric" className="form-control form-control-sm" aria-label="สูง" value={resizeH} onChange={(e) => changeResize("h", e.target.value)} />
            </div>
            <label className="d-flex align-items-center gap-2 small mb-2"><input type="checkbox" checked={resizeLock} onChange={(e) => setResizeLock(e.target.checked)} />ล็อกสัดส่วน</label>
            <button type="button" className="btn btn-primary btn-sm" onClick={applyResize}><i className="fa-solid fa-check me-1" />ใช้ขนาดนี้</button>
          </Section>
        </>
      );
    }
    if (tool === "adjust") {
      const a = doc.adj;
      return (
        <>
          <Slider label="ความสว่าง" value={a.brightness} min={-100} max={100} onChange={(v) => setAdjust("brightness", v)} />
          <Slider label="คอนทราสต์" value={a.contrast} min={-100} max={100} onChange={(v) => setAdjust("contrast", v)} />
          <Slider label="ความอิ่มสี" value={a.saturation} min={-100} max={100} onChange={(v) => setAdjust("saturation", v)} />
          <Section title="ฟิลเตอร์">
            <div className="d-flex flex-wrap gap-1">
              {FILTERS.map((f) => (
                <button key={f.key} type="button" className={`btn btn-sm ${a.filter === f.key ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setAdjust("filter", f.key)}>{f.label}</button>
              ))}
            </div>
          </Section>
          <Slider label="เบลอทั้งภาพ" value={a.blur} min={0} max={20} step={0.5} onChange={(v) => setAdjust("blur", v)} />
          <Slider label="ความคม" value={a.sharpen} min={0} max={100} onChange={(v) => setAdjust("sharpen", v)} />
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetAdjust}><i className="fa-solid fa-rotate-left me-1" />รีเซ็ต</button>
        </>
      );
    }
    if (tool === "redact" || selectedObj?.type === "redact") {
      const target = selectedObj?.type === "redact" ? selectedObj : null;
      const mode = target ? target.mode : redactMode;
      const strength = target ? target.strength : redactStrength;
      return (
        <>
          <div className="small text-secondary mb-3">ลากกรอบบนภาพเพื่อปิดข้อมูลส่วนตัว ผลจะฝังลงพิกเซลจริงตอนบันทึก</div>
          <Section title="รูปแบบ">
            <div className="d-flex flex-wrap gap-1">
              {[["blur", "เบลอ"], ["pixelate", "โมเสก"], ["black", "แถบดำ"]].map(([key, label]) => (
                <button key={key} type="button" className={`btn btn-sm ${mode === key ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => { setRedactMode(key); if (target) editSelected("mode", { mode: key }); }}>{label}</button>
              ))}
            </div>
          </Section>
          {mode !== "black" ? (
            <Slider label="ความแรง" value={strength} min={2} max={60}
              onChange={(v) => { setRedactStrength(v); if (target) editSelected("strength", { strength: v }); }} />
          ) : null}
        </>
      );
    }
    if (tool === "watermark") {
      const w = doc.wm;
      return (
        <>
          <label className="d-flex align-items-center gap-2 small mb-3 fw-semibold"><input type="checkbox" checked={w.enabled} onChange={(e) => setWm("enabled", e.target.checked)} />เปิดใช้ลายน้ำ</label>
          <input type="text" className="form-control form-control-sm mb-3" value={w.text} placeholder="ข้อความลายน้ำ" onChange={(e) => setWm("text", e.target.value)} />
          <Slider label="ขนาด (% ของด้านสั้น)" value={w.sizePct} min={2} max={30} step={0.5} onChange={(v) => setWm("sizePct", v)} />
          <Slider label="ความโปร่งใส" value={Math.round((1 - w.opacity) * 100)} min={0} max={95} suffix="%" onChange={(v) => setWm("opacity", 1 - v / 100)} />
          <Slider label="องศา" value={w.angle} min={-90} max={90} onChange={(v) => setWm("angle", v)} />
          <div className="d-flex align-items-center gap-3 mb-3 small">
            <label className="d-flex align-items-center gap-2">สี<input type="color" value={w.color} onChange={(e) => setWm("color", e.target.value)} style={{ width: 34, height: 28, padding: 0 }} /></label>
            <label className="d-flex align-items-center gap-2"><input type="checkbox" checked={w.tile} onChange={(e) => setWm("tile", e.target.checked)} />ทำซ้ำทั้งภาพ</label>
          </div>
          {!w.tile ? (
            <select className="form-select form-select-sm" value={w.position} onChange={(e) => setWm("position", e.target.value)}>
              <option value="center">ตรงกลาง</option><option value="tl">มุมซ้ายบน</option><option value="tr">มุมขวาบน</option>
              <option value="bl">มุมซ้ายล่าง</option><option value="br">มุมขวาล่าง</option>
            </select>
          ) : null}
        </>
      );
    }
    return (
      <>
        {showColor ? (
          <label className="d-flex align-items-center gap-2 small mb-3">สี
            <input type="color" value={selectedObj?.color || style.color} onChange={(e) => changeColor(e.target.value)} style={{ width: 34, height: 28, padding: 0, border: "1px solid #cbd5e1", borderRadius: 6 }} />
          </label>
        ) : null}
        {showWidth ? <Slider label="ความหนา" value={Math.round(widthValue * 10) / 10} min={1} max={(tool === "highlight" || selectedObj?.type === "highlight") ? 80 : 40} onChange={changeWidth} /> : null}
        {showTextSize ? (
          <label className="d-block small mb-3">ขนาดตัวอักษร (พิกเซล)
            <input type="number" className="form-control form-control-sm" min="4" max="4000" value={Math.round(selectedObj?.type === "text" ? selectedObj.size : style.textSize)} onChange={(e) => changeTextSize(e.target.value)} />
          </label>
        ) : null}
        {selectedObj?.type === "text" ? (
          <>
            <textarea ref={textAreaRef} className="form-control form-control-sm mb-2" rows={2} value={selectedObj.text} placeholder="พิมพ์ข้อความ" onChange={(e) => editSelected("text", { text: e.target.value })} />
            <label className="d-flex align-items-center gap-1 small mb-3"><input type="checkbox" checked={!!selectedObj.bold} onChange={(e) => editSelected("bold", { bold: e.target.checked })} />ตัวหนา</label>
          </>
        ) : null}
        {selectedObj?.type === "image" ? <Slider label="ความโปร่งใส" value={Math.round((selectedObj.opacity ?? 1) * 100)} min={10} max={100} suffix="%" onChange={(v) => editSelected("opacity", { opacity: v / 100 })} /> : null}
        {selectedIds.length ? (
          <div className="d-flex flex-wrap gap-2 align-items-center">
            {selectedIds.length > 1 ? <span className="small text-secondary">เลือก {selectedIds.length} ชิ้น</span> : null}
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={copySelected} title="คัดลอก (Ctrl/Cmd+C)"><i className="fa-solid fa-copy me-1" />คัดลอก</button>
            <button type="button" className="btn btn-outline-danger btn-sm" onClick={deleteSelected}><i className="fa-solid fa-trash me-1" />ลบที่เลือก</button>
          </div>
        ) : (
          <div className="small text-secondary">{isDrawTool ? "ลากหรือคลิกบนภาพเพื่อวาง" : "เลือกเครื่องมือทางซ้าย หรือคลิกออบเจกต์บนภาพเพื่อลากย้าย/ปรับขนาด"}</div>
        )}
      </>
    );
  })();

  const pageTool = ["crop", "transform", "adjust", "watermark"].includes(tool) ? "select" : tool;

  const content = (
    <div style={{ position: "fixed", inset: 0, zIndex: 1050, background: "#eef3f9", display: "flex", flexDirection: "column", color: INK }} role="dialog" aria-label="แก้ไขภาพ">
      <div className="d-flex align-items-center gap-2 px-3 py-2 bg-white flex-wrap" style={{ borderBottom: "1px solid rgba(18,125,255,0.12)" }}>
        <div className="d-flex align-items-center gap-2 me-auto" style={{ minWidth: 0 }}>
          <i className="fa-solid fa-image" style={{ color: "#8d5cf6", fontSize: 20 }} />
          <div className="fw-semibold text-truncate" style={{ maxWidth: 360 }}>{file.name}</div>
          {base ? <span className="small text-secondary">{base.width}×{base.height}</span> : null}
          {dirty ? <span className="badge" style={{ background: "rgba(226,113,29,0.15)", color: "#e2711d" }}>ยังไม่บันทึก</span> : null}
          {notes.map((n) => <span key={n} className="small text-secondary text-truncate"><i className="fa-solid fa-circle-info me-1" />{n}</span>)}
        </div>

        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={undo} disabled={!undoRef.current.length} title="ย้อนกลับ (Ctrl/Cmd+Z)"><i className="fa-solid fa-rotate-left me-1" />ย้อนกลับ</button>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={redo} disabled={!redoRef.current.length} title="ทำซ้ำ (Ctrl/Cmd+Shift+Z)"><i className="fa-solid fa-rotate-right me-1" />ทำซ้ำ</button>

        <div className="btn-group btn-group-sm" role="group" aria-label="ซูม">
          <button type="button" className="btn btn-outline-secondary" onClick={() => stepZoom(1 / 1.2)} title="ซูมออก"><i className="fa-solid fa-magnifying-glass-minus" /></button>
          <button type="button" className="btn btn-outline-secondary" style={{ minWidth: 64 }} onClick={fit} title="พอดีจอ">{Math.round(zoom * 100)}%</button>
          <button type="button" className="btn btn-outline-secondary" onClick={() => stepZoom(1.2)} title="ซูมเข้า"><i className="fa-solid fa-magnifying-glass-plus" /></button>
        </div>

        <Dropdown align="end">
          <Dropdown.Toggle size="sm" variant="primary" disabled={!doc || isSaving}><i className="fa-solid fa-floppy-disk me-1" />บันทึก</Dropdown.Toggle>
          <Dropdown.Menu style={{ zIndex: 1100 }}>
            <Dropdown.Item onClick={() => setSaveDialog("new")}><i className="fa-solid fa-file-circle-plus me-2" />บันทึกเป็นไฟล์ใหม่</Dropdown.Item>
            <Dropdown.Item disabled={!canOverwrite} onClick={() => handleSave("overwrite", overwriteMime, 92)} title={canOverwrite ? "" : "GIF บันทึกทับไม่ได้ ให้บันทึกเป็นไฟล์ใหม่"}>
              <i className="fa-solid fa-file-pen me-2" />บันทึกทับไฟล์เดิม
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => setSaveDialog("download")}><i className="fa-solid fa-download me-2" />ดาวน์โหลดลงเครื่อง</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={requestClose} title="ปิด"><i className="fa-solid fa-xmark me-1" />ปิด</button>
      </div>

      <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
        <div className="bg-white d-flex flex-column p-1 flex-shrink-0" style={{ width: 84, borderRight: "1px solid rgba(18,125,255,0.12)", overflowY: "auto" }}>
          <ToolButton icon="fa-arrow-pointer" label="เลือก" active={tool === "select"} onClick={() => chooseTool("select")} />
          <ToolButton icon="fa-crop-simple" label="ครอป" active={tool === "crop"} onClick={() => chooseTool("crop")} disabled={!doc} />
          <ToolButton icon="fa-up-right-and-down-left-from-center" label="หมุน/ขนาด" active={tool === "transform"} onClick={() => chooseTool("transform")} disabled={!doc} />
          <ToolButton icon="fa-sliders" label="ปรับสี" active={tool === "adjust"} onClick={() => chooseTool("adjust")} disabled={!doc} />
          <hr className="my-1" />
          <ToolButton icon="fa-signature" label="ลายเซ็น" onClick={() => setAssetType("signature")} disabled={!doc} />
          <ToolButton icon="fa-stamp" label="ตราปั๊ม" onClick={() => setAssetType("stamp")} disabled={!doc} />
          <ToolButton icon="fa-image" label="รูปภาพ" onClick={() => imageInputRef.current?.click()} disabled={!doc} />
          <ToolButton icon="fa-font" label="ข้อความ" active={tool === "text"} onClick={() => chooseTool("text")} disabled={!doc} />
          <Dropdown drop="end">
            <Dropdown.Toggle as="button" type="button" className="btn btn-sm d-flex flex-column align-items-center justify-content-center px-1 py-1 border-0 w-100" disabled={!doc}
              style={{ minHeight: 50, borderRadius: 10, background: "transparent", color: INK }}>
              <i className="fa-solid fa-calendar-day" style={{ fontSize: 16 }} /><span style={{ fontSize: 11, marginTop: 2 }}>วันที่</span>
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ zIndex: 1100 }}>
              <Dropdown.Item onClick={() => addDate("be")}>{formatToday("be")} (พ.ศ.)</Dropdown.Item>
              <Dropdown.Item onClick={() => addDate("ce")}>{formatToday("ce")} (ค.ศ.)</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <ToolButton icon="fa-pen" label="ปากกา" active={tool === "pen"} onClick={() => chooseTool("pen")} disabled={!doc} />
          <ToolButton icon="fa-highlighter" label="ไฮไลต์" active={tool === "highlight"} onClick={() => chooseTool("highlight")} disabled={!doc} />
          <ToolButton icon="fa-square" label="กล่อง" active={tool === "rect"} onClick={() => chooseTool("rect")} disabled={!doc} />
          <ToolButton icon="fa-copy" label="คัดลอก" onClick={copySelected} disabled={!selectedIds.length} />
          <ToolButton icon="fa-paste" label="วาง" onClick={pasteClip} disabled={!doc || !hasClipboard()} />
          <hr className="my-1" />
          <ToolButton icon="fa-eye-slash" label="ปิดบัง" active={tool === "redact"} onClick={() => chooseTool("redact")} disabled={!doc} />
          <ToolButton icon="fa-droplet" label="ลายน้ำ" active={tool === "watermark"} onClick={() => chooseTool("watermark")} disabled={!doc} />
        </div>

        <div ref={areaRef} className="flex-grow-1 d-flex" style={{ overflow: "auto", padding: 24 }}>
          {doc && previewCanvas ? (
            <div style={{ margin: "auto" }}>
              {tool === "crop" && cropRect ? (
                <CropOverlay
                  preview={previewCanvas}
                  width={base.width}
                  height={base.height}
                  zoom={zoom}
                  rect={cropRect}
                  ratio={ASPECTS.find((a) => a.key === cropAspect)?.ratio || null}
                  onChange={setCropRect}
                />
              ) : (
                <PageView
                  page={page}
                  pdfDoc={null}
                  index={0}
                  zoom={zoom}
                  tool={pageTool}
                  color={style.color}
                  width={style.width * unit}
                  textSize={style.textSize}
                  active={false}
                  selectedIds={selectedIds}
                  onActivate={() => {}}
                  onSelect={setSelectedIds}
                  onBeginEdit={pushHistory}
                  onUpdateMany={updateMany}
                  onAdd={addObject}
                  bitmapSource={previewCanvas}
                  hideLabel
                  redactMode={redactMode}
                  redactStrength={redactStrength * unit}
                />
              )}
            </div>
          ) : null}
        </div>

        <div className="bg-white flex-shrink-0 p-3" style={{ width: 280, borderLeft: "1px solid rgba(18,125,255,0.12)", overflowY: "auto" }}>
          <div className="fw-semibold mb-3" style={{ color: PRIMARY }}>
            {{ select: "ออบเจกต์", crop: "ครอปภาพ", transform: "หมุน / พลิก / ขนาด", adjust: "ปรับแสงและสี", redact: "ปิดบังเฉพาะส่วน", watermark: "ลายน้ำ", text: "ข้อความ", pen: "ปากกา", highlight: "ไฮไลต์", rect: "กล่อง" }[tool]}
          </div>
          {panel}
        </div>
      </div>

      <input ref={imageInputRef} type="file" accept="image/*" hidden onChange={handleImageFile} />
      <AssetLibraryModal type={assetType || "signature"} show={!!assetType} onHide={() => setAssetType(null)} onPick={handlePickAsset} />

      <Modal show={!!saveDialog} onHide={() => setSaveDialog(null)} centered>
        <Modal.Header closeButton><Modal.Title as="h5" className="fw-semibold">{saveDialog === "download" ? "ดาวน์โหลดลงเครื่อง" : "บันทึกเป็นไฟล์ใหม่"}</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="fw-semibold small mb-2">ชนิดไฟล์</div>
          <div className="d-flex gap-2 mb-3">
            {FORMATS.map((f) => (
              <button key={f.mime} type="button" className={`btn btn-sm ${fmt === f.mime ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setFmt(f.mime)}>{f.label}</button>
            ))}
          </div>
          {fmtInfo.lossy ? <Slider label="คุณภาพ" value={quality} min={40} max={100} suffix="%" onChange={setQuality} /> : null}
          {fmt === "image/jpeg" ? <div className="small text-secondary mb-2">JPG ไม่มีพื้นโปร่งใส ส่วนโปร่งใสจะถูกเติมสีขาว</div> : null}
          <div className="small text-secondary">ชื่อไฟล์: {nameFor(fmt)}</div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setSaveDialog(null)}>ยกเลิก</button>
          <button type="button" className="btn btn-primary" onClick={() => { const mode = saveDialog; setSaveDialog(null); handleSave(mode === "download" ? "download" : "new", fmt, quality); }}>
            {saveDialog === "download" ? "ดาวน์โหลด" : "บันทึก"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );

  return createPortal(content, document.body);
}
